import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import {
  Anchor,
  Building2,
  LayoutDashboard,
  Ship,
  Navigation,
  ClipboardCheck,
  Calculator,
  Map,
  ClipboardList,
  BarChart3,
  Flame,
  FileSpreadsheet,
  Clock,
  Mail,
  Users,
  CalendarRange,
  ShieldCheck,
  Droplets,
  FlaskConical,
  Waves,
  ArrowLeftRight,
} from 'lucide-react'

export type ModuleId = 'home' | 'lng-ops' | 'fsru'

export interface NavItem {
  to: string
  icon: LucideIcon
  label: string
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

export interface ModuleDefinition {
  id: ModuleId
  label: string
  description: string
  icon: LucideIcon
  accentColor: string
  basePath: string
  isAvailable: boolean
  navGroups: NavGroup[]
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    id: 'lng-ops',
    label: 'LNG Operations',
    description: 'Fleet management, voyage performance, and commercial operations',
    icon: Anchor,
    accentColor: '#00b7ea',
    basePath: '/lng-ops',
    isAvailable: true,
    navGroups: [
      {
        title: 'Fleet',
        items: [
          { to: '/lng-ops', icon: LayoutDashboard, label: 'Dashboard' },
          { to: '/lng-ops/live-view', icon: Map, label: 'Live View' },
          { to: '/lng-ops/vessels', icon: Ship, label: 'Vessels' },
          { to: '/lng-ops/itinerary', icon: CalendarRange, label: 'Itinerary' },
        ],
      },
      {
        title: 'Operations',
        items: [
          { to: '/lng-ops/voyage-performance', icon: Navigation, label: 'Voyage Perf.' },
          { to: '/lng-ops/bog-management', icon: Flame, label: 'BOG Management' },
          { to: '/lng-ops/vessel-reports', icon: ClipboardList, label: 'Vessel Reports' },
        ],
      },
      {
        title: 'Analysis',
        items: [
          { to: '/lng-ops/graphs', icon: BarChart3, label: 'Graphs' },
          { to: '/lng-ops/reports', icon: FileSpreadsheet, label: 'Reports' },
          { to: '/lng-ops/scheduler', icon: Clock, label: 'Scheduler' },
        ],
      },
      {
        title: 'Tools',
        items: [
          { to: '/lng-ops/sscs', icon: ClipboardCheck, label: 'SSCS' },
          { to: '/lng-ops/calculators', icon: Calculator, label: 'Calculators' },
        ],
      },
      {
        title: '',
        items: [
          { to: '/lng-ops/messages', icon: Mail, label: 'Messages' },
          { to: '/lng-ops/charterers', icon: Users, label: 'Charterers' },
        ],
      },
    ],
  },
  {
    id: 'fsru',
    label: 'FSRU',
    description: 'Regasification, cargo management, gas quality, and STS operations',
    icon: Building2,
    accentColor: '#a855f7',
    basePath: '/fsru',
    isAvailable: true,
    navGroups: [
      {
        title: 'Overview',
        items: [
          { to: '/fsru', icon: LayoutDashboard, label: 'Dashboard' },
        ],
      },
      {
        title: 'Operations',
        items: [
          { to: '/fsru/regasification', icon: Waves, label: 'Regasification' },
          { to: '/fsru/cargo', icon: Droplets, label: 'Cargo' },
          { to: '/fsru/quality', icon: FlaskConical, label: 'Gas Quality' },
        ],
      },
      {
        title: 'Performance',
        items: [
          { to: '/fsru/performance', icon: ShieldCheck, label: 'Performance' },
          { to: '/fsru/sts', icon: ArrowLeftRight, label: 'STS Operations' },
        ],
      },
    ],
  },
]

export function useActiveModule(): ModuleId {
  const location = useLocation()

  return useMemo(() => {
    const path = location.pathname
    if (path.startsWith('/lng-ops')) return 'lng-ops'
    if (path.startsWith('/fsru')) return 'fsru'
    return 'home'
  }, [location.pathname])
}

export function getModuleDefinition(id: ModuleId): ModuleDefinition | undefined {
  return MODULE_DEFINITIONS.find((m) => m.id === id)
}
